/**
 * What the "New group" form on the home screen is allowed to send.
 *
 * The server decides this too — `server/groups.ts` reads the name through
 * `text(…, MAX_GROUP_NAME)` and the currency through `currencyOf`, and no
 * answer here can talk it out of either. What this module buys is the
 * sentence: a person who typed four letters into the currency box should be
 * told so while the box is still in front of them, rather than after a round
 * trip that comes back as `bad_request`.
 *
 * It is pure — no React, no `fetch` — so the rule is testable as what it is,
 * a function from two strings to a draft or a complaint, and so the form has
 * exactly one place where "is this sendable" is decided.
 */

/** `MAX_GROUP_NAME` in `server/groups.ts`. The column is the same length. */
export const MAX_GROUP_NAME = 100

/**
 * What the server falls back to when a create omits the currency, restated
 * here because the form has to put *something* in the field: a box that shows
 * nothing and means USD is a box that will eventually make somebody a group
 * in the wrong currency.
 */
export const DEFAULT_CURRENCY = 'USD'

export type GroupDraft = { name: string; currency: string }

export type DraftReading =
  | { ok: true; draft: GroupDraft }
  /** Something to show next to the field, in words, before anything is sent. */
  | { ok: false; problem: string }

/**
 * Reads the two fields.
 *
 * The currency is upper-cased rather than rejected for its case — `usd` is a
 * person typing, not a person being wrong — and it is checked for shape only.
 * Nothing here holds a list of currencies, for the reason `currencyOf` gives:
 * a list of currencies goes out of date, and which three letters a group's
 * members have agreed on is not this form's argument to have.
 */
export function readGroupDraft(name: string, currency: string): DraftReading {
  const trimmed = name.trim()
  if (trimmed.length === 0) return { ok: false, problem: 'Give the group a name.' }
  if (trimmed.length > MAX_GROUP_NAME) {
    return { ok: false, problem: `A name can be at most ${MAX_GROUP_NAME} characters.` }
  }

  const code = currency.trim()
  if (!/^[A-Za-z]{3}$/.test(code)) {
    return { ok: false, problem: 'A currency is a three-letter code, like USD or EUR.' }
  }

  return { ok: true, draft: { name: trimmed, currency: code.toUpperCase() } }
}

/**
 * What to put in the currency box before anybody touches it.
 *
 * Somebody's second group is very nearly always in the currency of their
 * first, and `PATCH /api/groups/:id` refuses a currency change once the group
 * has any transactions — so the field is worth pre-filling with an answer
 * that is usually right rather than with one that is merely constant. Groups
 * in more than one currency have no majority worth guessing at, so that falls
 * back to the default like an empty list does.
 */
export function suggestedCurrency(currencies: readonly string[]): string {
  const distinct = [...new Set(currencies)]
  return distinct.length === 1 ? distinct[0] : DEFAULT_CURRENCY
}
